import { assign } from 'xstate';

import {
  checkIfInStock,
  checkIfModificationGroupNotEmpty,
  extendModifierGroup,
  extractModificationsLimits,
  generateInitialIngredientQuantities,
  generateModificationsList,
  generateModificationsMap,
  generatePairedModifierGroupMaps,
  mergePairedModifierGroups,
  resolveModification,
} from '../../helpers';
import {
  type Context,
  type LineageModificationsMap,
  type CustomizationMachineAction,
  type ModificationsMap,
  type ProductLineItem,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates derived modifications based on the provided product and optional line item.
 */
export const generateModifications: CustomizationMachineAction = assign((args) => {
  const { product, productLineItem, sharedLineItemId, isUpsellPricingEnabled } = args.context;

  if (!product) return {};

  const { modifierGroups, ingredients } = product;

  const mergedModifierGroups = isUpsellPricingEnabled
    ? mergePairedModifierGroups(modifierGroups).mergedGroups
    : modifierGroups;

  const initialIngredients = productLineItem?.ingredients ?? ingredients;
  const quantity = productLineItem?.quantity ?? 1;

  const { allModificationsMap, includedModificationsMap, extrasModificationsMap, lineageModificationsMap } = generateModificationsMap(modifierGroups); // prettier-ignore

  // NOTE: A shared line item may come from another restaurant, where ingredient ids differ.
  //       In that flow we fall back to lineage keys.
  const sharedLineageModificationsMap = sharedLineItemId ? lineageModificationsMap : undefined;

  const modificationGroups = mergedModifierGroups
    .filter(checkIfModificationGroupNotEmpty)
    .map(extendModifierGroup);
  const initialIngredientQuantities = generateInitialIngredientQuantities(
    ingredients,
    allModificationsMap,
  );
  const initialModifications = generateModificationsList(ingredients, allModificationsMap);
  const initialAndAddedModifications = generateModificationsList(
    initialIngredients,
    allModificationsMap,
    sharedLineageModificationsMap,
  );
  const modificationLimits = extractModificationsLimits(modifierGroups);
  const activeModifications = initialAndAddedModifications.modificationsList.filter(checkIfInStock);

  const { includedModifierGroupsByExtrasName, extrasModifierGroupsByIncludedName } =
    generatePairedModifierGroupMaps(modifierGroups);

  // ─────────────────────────────────────────────────────────────────────

  const modifications: Context['modifications'] = {
    initial: initialModifications.modificationsList,
    active: activeModifications,
    additions: [],
    removals: [],
    substitutions: [],
  };

  return {
    allModificationsMap,
    initialIngredientQuantities,
    includedModificationsMap,
    extrasModificationsMap,
    includedModifierGroupsByExtrasName,
    extrasModifierGroupsByIncludedName,
    modificationGroups,
    modifications,
    modificationLimits,
    quantity,
    customName: productLineItem?.customName ?? undefined,

    ...(productLineItem?.mixedDressingDetails
      ? {
          mixedDressingDetails: resolveMixedDressingDetails(
            productLineItem,
            allModificationsMap,
            sharedLineageModificationsMap,
          ),
        }
      : {}),
  };
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Points each dressing detail at the ingredient id used by the current product,
 * resolving by lineage key when the line item's id is not on this menu. The
 * dressing's lineage key comes from the matching line item ingredient.
 */
function resolveMixedDressingDetails(
  productLineItem: NonNullable<ProductLineItem>,
  allModificationsMap: ModificationsMap,
  lineageModificationsMap?: LineageModificationsMap,
) {
  const { ingredients, mixedDressingDetails } = productLineItem;

  return mixedDressingDetails.map((dressingDetail) => {
    const { ingredientId, weight } = dressingDetail;

    const lineItemIngredient = findLineItemIngredient(ingredients, ingredientId);

    const lookupData = {
      id: lineItemIngredient?.id ?? ingredientId,
      lineageKey: lineItemIngredient?.lineageKey ?? null,
    };

    const modification = resolveModification(
      lookupData,
      allModificationsMap,
      lineageModificationsMap,
    );
    const resolvedIngredientId = modification?.ingredient?.id ?? ingredientId;

    return { ingredientId: resolvedIngredientId, weight };
  });
}

/**
 * Finds the line item ingredient a dressing detail refers to. Dressing details
 * are not always encoded the same way as the ingredient list, so an exact id
 * match falls back to comparing raw ingredient ids.
 */
function findLineItemIngredient(
  ingredients: NonNullable<ProductLineItem>['ingredients'],
  ingredientId: string,
) {
  const exactMatch = ingredients.find((ingredient) => ingredient.id === ingredientId);

  if (exactMatch) {
    return exactMatch;
  }

  const rawIngredientId = getRawIngredientId(ingredientId);

  return ingredients.find((ingredient) => getRawIngredientId(ingredient.id) === rawIngredientId);
}

/**
 * Strips the modifier group prefix that included pricing encodes into an
 * ingredient id, leaving the underlying ingredient id.
 */
function getRawIngredientId(ingredientId: string) {
  return String(Number(ingredientId) % ENCODED_INGREDIENT_ID_BASE);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ENCODED_INGREDIENT_ID_BASE = 100_000_000;
